import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import type { GoalStatus } from '@/features/hrm/development/api';
import * as api from './api';
import type { CabinetSkillInput, CabinetTimeOffInput } from './types';

export const cabinetKeys = {
  all: ['cabinet'] as const,
  profile: () => [...cabinetKeys.all, 'profile'] as const,
  salary: () => [...cabinetKeys.all, 'salary'] as const,
  payslips: () => [...cabinetKeys.all, 'payslips'] as const,
  reviews: () => [...cabinetKeys.all, 'reviews'] as const,
  pdp: () => [...cabinetKeys.all, 'pdp'] as const,
  timeOff: () => [...cabinetKeys.all, 'time-off'] as const,
  onboarding: () => [...cabinetKeys.all, 'onboarding'] as const,
  skills: () => [...cabinetKeys.all, 'skills'] as const,
  invoices: () => [...cabinetKeys.all, 'invoices'] as const,
  documents: () => [...cabinetKeys.all, 'documents'] as const,
  bankAccounts: () => [...cabinetKeys.all, 'bank-accounts'] as const,
};

export function useCabinetProfile() {
  return useQuery({
    queryKey: cabinetKeys.profile(),
    queryFn: api.getCabinetProfile,
    // A missing employee profile (404) is a real "no data" state, not a
    // transient error — don't hammer it with retries.
    retry: false,
  });
}

export function useCabinetSalary(params: api.CabinetListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.salary(), params],
    queryFn: () => api.getCabinetSalary(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useCabinetPayslips(params: api.CabinetListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.payslips(), params],
    queryFn: () => api.getCabinetPayslips(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useCabinetReviews(params: api.CabinetReviewListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.reviews(), params],
    queryFn: () => api.getCabinetReviews(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useCabinetReview(publicId: string) {
  return useQuery({
    queryKey: [...cabinetKeys.reviews(), 'detail', publicId],
    queryFn: () => api.getCabinetReview(publicId),
    enabled: Boolean(publicId),
    retry: false,
  });
}

export function useAcknowledgeCabinetReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.acknowledgeCabinetReview(publicId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.reviews() });
      toast.success('Review acknowledged.');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useSetCabinetReviewActionItemDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isDone }: { id: number; isDone: boolean }) =>
      api.setCabinetReviewActionItemDone(id, isDone),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.reviews() });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useCabinetPdps(params: api.CabinetListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.pdp(), params],
    queryFn: () => api.getCabinetPdps(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useCabinetPdp(publicId: string) {
  return useQuery({
    queryKey: [...cabinetKeys.pdp(), 'detail', publicId],
    queryFn: () => api.getCabinetPdp(publicId),
    enabled: Boolean(publicId),
    retry: false,
  });
}

export function useSetCabinetPdpGoalStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: GoalStatus }) =>
      api.setCabinetPdpGoalStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.pdp() });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}


// ---- Time Off ----
export function useCabinetTimeOff(params: api.CabinetTimeOffListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.timeOff(), params],
    queryFn: () => api.listCabinetTimeOff(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useCreateCabinetTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CabinetTimeOffInput) => api.createCabinetTimeOff(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.timeOff() });
      toast.success('Time-off request submitted');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateCabinetTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: Partial<CabinetTimeOffInput> }) =>
      api.updateCabinetTimeOff(publicId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.timeOff() });
      toast.success('Request updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useCancelCabinetTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.cancelCabinetTimeOff(publicId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.timeOff() });
      toast.success('Request cancelled');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Onboarding ----
export function useCabinetOnboarding(params: api.CabinetListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.onboarding(), params],
    queryFn: () => api.getCabinetOnboarding(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useCabinetOnboardingPlan(publicId: string) {
  return useQuery({
    queryKey: [...cabinetKeys.onboarding(), 'detail', publicId],
    queryFn: () => api.getCabinetOnboardingPlan(publicId),
    enabled: Boolean(publicId),
    retry: false,
  });
}

export function useMarkCabinetOnboardingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isDone }: { id: number; isDone: boolean }) =>
      api.markCabinetOnboardingItem(id, isDone),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.onboarding() });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useStartCabinetOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.startCabinetOnboarding(publicId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.onboarding() });
      toast.success('Onboarding started.');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Skills ----
export function useCabinetSkills(params: api.CabinetListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.skills(), params],
    queryFn: () => api.listCabinetSkills(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useCreateCabinetSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CabinetSkillInput) => api.createCabinetSkill(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.skills() });
      toast.success('Skill added');
    },
    onError: (err: ApiError) => {
      // The inline add form has no field-error UI, so surface it as a toast.
      toast.error(err.fieldErrors?.skill?.[0] ?? err.message);
    },
  });
}

export function useUpdateCabinetSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<CabinetSkillInput> }) =>
      api.updateCabinetSkill(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.skills() });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteCabinetSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteCabinetSkill(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.skills() });
      toast.success('Skill removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/** Distinct, non-empty categories already used in the org skill catalog — feeds
 *  the inline category editor's autocomplete so naming stays consistent. */
export function useCabinetSkillCategories() {
  return useQuery({
    queryKey: [...cabinetKeys.skills(), 'categories'],
    queryFn: async () => {
      const skills = await api.getCabinetSkillCatalog();
      return Array.from(new Set(skills.map((s) => s.category).filter(Boolean))).sort();
    },
    staleTime: 60_000,
  });
}

export function useSetCabinetSkillCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, category }: { skillId: number; category: string }) =>
      api.setCabinetSkillCategory(skillId, category),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.skills() });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Bank accounts (read-only — Finance/HR maintain them) ----
export function useCabinetBankAccounts(params: api.CabinetListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.bankAccounts(), params],
    queryFn: () => api.listCabinetBankAccounts(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

// ---- Invoices ----
export function useCabinetInvoices(params: api.CabinetListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.invoices(), params],
    queryFn: () => api.listCabinetInvoices(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useCabinetInvoice(publicId: string) {
  return useQuery({
    queryKey: [...cabinetKeys.invoices(), 'detail', publicId],
    queryFn: () => api.getCabinetInvoice(publicId),
    enabled: Boolean(publicId),
    retry: false,
  });
}

export function useSignCabinetInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.signCabinetInvoice(publicId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.invoices() });
      toast.success('Invoice signed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Documents ----
export function useCabinetDocuments(params: api.CabinetListParams = {}) {
  return useQuery({
    queryKey: [...cabinetKeys.documents(), params],
    queryFn: () => api.listCabinetDocuments(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useSignCabinetDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.signCabinetDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cabinetKeys.documents() });
      toast.success('Document signed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
